import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Button, HelpTooltip, Tooltip } from '~/components';
import { useApi } from '~/contexts';
import { devices } from '~/styles';

const TimeZoneSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;

  @media ${devices.touch} {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
`;

const TimeZoneContainer = styled.span`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
`;

const LocalTimeZone = styled.span`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.2rem;
`;

const WorkspaceTimeZone = styled.span`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.25rem;
`;

const StyledButton = styled(Button)`
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
`;

export default function TimeZoneArea({ workspace, workspaceTimeZone, timeZoneId, setTimeZoneId }) {
  const api = useApi();
  const [timeZones, setTimeZones] = useState([]);
  const localTimeZoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const normalizedLocalTimeZoneName =
    localTimeZoneName === 'UTC' ? 'Etc/UTC' : localTimeZoneName === 'GMT' ? 'Etc/GMT' : localTimeZoneName;

  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        const { data } = await api.timeZones().get();
        if (!isMounted) return;
        setTimeZones(data);
      } catch (error) {
        if (!isMounted) return;
        setTimeZones([]);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [api]);

  const localTimeZone = useMemo(() => {
    if (!normalizedLocalTimeZoneName || !timeZones.length) return null;

    const city = normalizedLocalTimeZoneName.split('/').pop();
    const matches = (tz) =>
      tz.name === normalizedLocalTimeZoneName ||
      tz.text === normalizedLocalTimeZoneName ||
      (!!city && (tz.name?.endsWith(`/${city}`) || tz.text?.endsWith(`/${city}`)));

    return timeZones.find(matches) || null;
  }, [normalizedLocalTimeZoneName, timeZones]);

  const localTimeZoneId = localTimeZone?.id ?? workspaceTimeZone?.id;

  const disabled = !localTimeZoneId || timeZoneId === localTimeZoneId;
  const tooltipMessage = disabled && localTimeZoneId ? 'Your local time zone is already selected.' : null;

  const handleTimeZone = () => {
    if (!localTimeZoneId) return;
    setTimeZoneId(localTimeZoneId);
  };

  return (
    <TimeZoneSection>
      <TimeZoneContainer>
        <LocalTimeZone>
          <span>Set to local time zone:</span>
          <Tooltip message={tooltipMessage}>
            <StyledButton isAnchor disabled={disabled} onClick={handleTimeZone}>
              {localTimeZone?.text || workspaceTimeZone?.text || 'US/Eastern'}
            </StyledButton>
          </Tooltip>
          {!disabled && (
            <HelpTooltip
              message="Time zone settings may affect timers.
             Please keep your time zone set to your current location."
            />
          )}
        </LocalTimeZone>
        <WorkspaceTimeZone>
          <span>
            {workspace.name}: {workspaceTimeZone ? workspaceTimeZone.text : 'US/Eastern'}
          </span>
          <HelpTooltip message="If a time zone is not set in your profile, the workspace time zone will be used." />
        </WorkspaceTimeZone>
      </TimeZoneContainer>
    </TimeZoneSection>
  );
}
